import { LogoutOutlined, MenuOutlined, UserOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { App, Button, Drawer, Layout, Menu, Popover, Space, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router';

import { useAuthLogout } from '@/api/generated/endpoints/auth/auth';
import { rememberMe, useCurrentUser } from '@/auth/useCurrentUser';
import { ApiHealthBadge } from '@/components/ApiHealthBadge';
import { ScanButton } from '@/components/PalletScanner';
import { menuItems } from '@/components/menu';
import { changeLanguage, currentLanguage, isLanguage } from '@/i18n';
import { LanguageSwitch } from '@/i18n/LanguageSwitch';
import { useIsNarrow } from '@/lib/useIsMobile';
import { useOutbox } from '@/offline/context';
import { OfflineBanner } from '@/offline/OfflineBanner';
import { UpdateBanner } from '@/components/UpdateBanner';
import { androidApp } from '@/runtime';
import { OutboxProvider } from '@/offline/OutboxProvider';
import { HOME, SECTIONS } from '@/sections';

export function AppLayout() {
  return (
    <OutboxProvider>
      <Shell />
    </OutboxProvider>
  );
}

function Shell() {
  const { t } = useTranslation();
  const { user } = useCurrentUser();
  // Язык из профиля главнее языка браузера: человек сел за другой компьютер (ADR-0008).
  const profileLocale = user?.locale;
  useEffect(() => {
    if (isLanguage(profileLocale) && profileLocale !== currentLanguage()) {
      void changeLanguage(profileLocale);
    }
  }, [profileLocale]);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { modal } = App.useApp();
  const outbox = useOutbox();
  // Телефон и узкий планшет (T-176): меню — «бургером», пользователь — в выпадающем окне.
  const narrow = useIsNarrow();
  const [menuOpen, setMenuOpen] = useState(false);
  const logout = useAuthLogout({
    mutation: {
      onSettled: () => {
        // Данные прошлого пользователя не должны мелькнуть следующему.
        rememberMe(null);
        queryClient.clear();
        void navigate('/login', { replace: true });
      },
    },
  });
  if (!user) return null; // RequireAuth уже перенаправил на вход

  const selected =
    location.pathname === HOME
      ? HOME
      : SECTIONS.find((s) => location.pathname.startsWith(s.path))?.path;

  function signOut() {
    // Очередь привязана к пользователю: она дождётся его следующего входа.
    if (outbox.ops.length === 0) {
      logout.mutate();
      return;
    }
    modal.confirm({
      title: t('offline.logoutTitle', { count: outbox.ops.length }),
      content: t('offline.logoutHint'),
      okText: t('auth.signOut'),
      cancelText: t('common.cancel'),
      autoFocusButton: 'cancel',
      onOk: () => {
        logout.mutate();
      },
    });
  }

  // Приложение для Android (T-173): адрес сервера хранит само приложение.
  const android = androidApp();

  const menu = (
    <Menu
      mode="inline"
      items={menuItems(t, user.role)}
      selectedKeys={selected ? [selected] : []}
      onClick={({ key }) => {
        setMenuOpen(false);
        void navigate(key);
      }}
    />
  );
  const signOutButton = (
    <Button icon={<LogoutOutlined />} loading={logout.isPending} onClick={signOut}>
      {t('auth.signOut')}
    </Button>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {!narrow && (
        <Layout.Sider width={220} theme="light">
          <Typography.Title level={5} style={{ padding: '16px 24px', margin: 0 }}>
            {t('app.brand')}
          </Typography.Title>
          {menu}
        </Layout.Sider>
      )}
      {narrow && (
        <Drawer
          placement="left"
          open={menuOpen}
          onClose={() => {
            setMenuOpen(false);
          }}
          title={t('app.brand')}
          width={280}
          styles={{ body: { padding: 0 } }}
        >
          {menu}
        </Drawer>
      )}
      <Layout>
        <Layout.Header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            background: '#fff',
            padding: narrow ? '0 8px' : '0 16px',
          }}
        >
          {narrow ? (
            <Space size={4}>
              <Button
                type="text"
                size="large"
                icon={<MenuOutlined />}
                aria-label={t('app.menu')}
                onClick={() => {
                  setMenuOpen(true);
                }}
              />
              <Typography.Text strong>{t('app.brand')}</Typography.Text>
            </Space>
          ) : (
            <ApiHealthBadge />
          )}
          {narrow ? (
            <Space size={0}>
              <ScanButton compact />
              <Popover
                trigger="click"
                placement="bottomRight"
                content={
                  <Space direction="vertical">
                    <Typography.Text strong>{user.full_name}</Typography.Text>
                    <Typography.Text type="secondary">{t(`roles.${user.role}`)}</Typography.Text>
                    <ApiHealthBadge />
                    <LanguageSwitch saveToProfile />
                    {android && (
                      <Button
                        onClick={() => {
                          android.changeServer();
                        }}
                      >
                        {t('server.change')}
                      </Button>
                    )}
                    {signOutButton}
                  </Space>
                }
              >
                <Button
                  type="text"
                  size="large"
                  icon={<UserOutlined />}
                  aria-label={t('app.account')}
                />
              </Popover>
            </Space>
          ) : (
            <Space>
              <ScanButton compact={false} />
              <UserOutlined />
              <span>{user.full_name}</span>
              <Typography.Text type="secondary">{t(`roles.${user.role}`)}</Typography.Text>
              <LanguageSwitch saveToProfile />
              {android && (
                <Button
                  onClick={() => {
                    android.changeServer();
                  }}
                >
                  {t('server.change')}
                </Button>
              )}
              {signOutButton}
            </Space>
          )}
        </Layout.Header>
        <Layout.Content style={{ padding: narrow ? 8 : 16, minWidth: 0 }}>
          <UpdateBanner />
          <OfflineBanner />
          <Outlet />
        </Layout.Content>
      </Layout>
    </Layout>
  );
}
